import type { ReactNode } from 'react'
import styles from './Page.module.css'

interface PageHeaderProps {
  title: string
  /** One line explaining what this screen is for. */
  description?: ReactNode
  actions?: ReactNode
}

export function PageHeader({ title, description, actions }: PageHeaderProps) {
  return (
    <header className={styles.header}>
      <div className={styles.headerText}>
        <h1 className={styles.title}>{title}</h1>
        {description && <p className={styles.description}>{description}</p>}
      </div>
      {actions && <div className={styles.actions}>{actions}</div>}
    </header>
  )
}

interface CardProps {
  title?: string
  description?: ReactNode
  actions?: ReactNode
  children: ReactNode
  /** Removes body padding. For a card that only contains a table. */
  flush?: boolean
}

export function Card({
  title,
  description,
  actions,
  children,
  flush = false,
}: CardProps) {
  return (
    <section className={styles.card}>
      {(title || actions) && (
        <div className={styles.cardHeader}>
          <div>
            {title && <h2 className={styles.cardTitle}>{title}</h2>}
            {description && (
              <p className={styles.cardDescription}>{description}</p>
            )}
          </div>
          {actions && <div className={styles.cardActions}>{actions}</div>}
        </div>
      )}
      <div className={flush ? undefined : styles.cardBody}>{children}</div>
    </section>
  )
}

/**
 * A callout for something the reader must not miss.
 *
 * Used sparingly and always for a CONSEQUENCE — a password that cannot be
 * retrieved, a setting that locks students out. If everything is highlighted,
 * nothing is.
 */
export function Callout({
  tone = 'info',
  title,
  children,
}: {
  tone?: 'info' | 'warning' | 'danger' | 'success'
  title?: string
  children: ReactNode
}) {
  return (
    <div className={`${styles.callout} ${styles[tone]}`} role="note">
      {title && <p className={styles.calloutTitle}>{title}</p>}
      <div className={styles.calloutBody}>{children}</div>
    </div>
  )
}

/** Toolbar above a table: filters on the left, actions on the right. */
export function Toolbar({ children }: { children: ReactNode }) {
  return <div className={styles.toolbar}>{children}</div>
}

export function PageStack({ children }: { children: ReactNode }) {
  return <div className={styles.stack}>{children}</div>
}
